import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Package, Plus, Grid3X3, Image, Star, Square } from 'lucide-react';
import AdminLayout from './AdminLayout';

// Placeholder collection previews for the empty grid state
const PLACEHOLDER_COLLECTIONS = [
  { name: 'Summer Icons', count: 24, type: 'Icons', color: 'from-amber-400 to-orange-500', icon: Star },
  { name: 'Memphis Shapes Vol.1', count: 36, type: 'Shapes', color: 'from-violet-400 to-fuchsia-500', icon: Square },
  { name: 'Flat UI Kit', count: 18, type: 'Mixed', color: 'from-blue-400 to-cyan-500', icon: Grid3X3 },
  { name: 'Social Media Pack', count: 12, type: 'Graphics', color: 'from-emerald-400 to-teal-500', icon: Image },
];

const AddCollectionCard = ({ onClick }) => (
  <button
    onClick={onClick}
    className="group w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-gray-200 hover:border-violet-400 hover:bg-violet-50/50 transition-all duration-200 flex flex-col items-center justify-center gap-3"
  >
    <div className="w-12 h-12 rounded-xl bg-gray-100 group-hover:bg-violet-100 flex items-center justify-center transition-colors">
      <Plus className="w-6 h-6 text-gray-400 group-hover:text-violet-500 transition-colors" />
    </div>
    <div className="text-center">
      <p className="text-sm font-medium text-gray-500 group-hover:text-violet-600 transition-colors">
        New Collection
      </p>
      <p className="text-xs text-gray-400 mt-0.5">Group elements into themed packs</p>
    </div>
  </button>
);

const PlaceholderCard = ({ collection }) => {
  const Icon = collection.icon;
  return (
    <div className="group relative rounded-2xl overflow-hidden bg-white border border-gray-100 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 cursor-pointer opacity-40">
      <div className={`aspect-[4/3] bg-gradient-to-br ${collection.color} flex items-center justify-center`}>
        <Icon className="w-12 h-12 text-white/60" />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold text-gray-900 text-sm">{collection.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">{collection.count} elements</p>
          </div>
          <Badge variant="outline" className="text-[11px] border-gray-200 text-gray-400">
            {collection.type}
          </Badge>
        </div>
      </div>
    </div>
  );
};

const AdminCollections = () => {
  const [collections] = useState([]);

  return (
    <AdminLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Collections</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Group related elements into curated packs like "Summer Icons" or "Memphis Shapes Vol.1".
            </p>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700 gap-1.5 flex-shrink-0">
            <Plus className="w-4 h-4" />
            New Collection
          </Button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Collections', value: collections.length },
            { label: 'Total Elements', value: 0 },
            { label: 'Published', value: 0 },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-5 py-4">
              <p className="text-2xl font-bold text-gray-900 tabular-nums">{s.value}</p>
              <p className="text-sm text-gray-500 font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Collections Grid */}
        {collections.length === 0 ? (
          <>
            {/* Empty state info */}
            <div className="flex items-center gap-3 bg-violet-50 border border-violet-100 rounded-xl px-5 py-3.5">
              <Package className="w-5 h-5 text-violet-500 flex-shrink-0" />
              <p className="text-sm text-violet-700">
                <span className="font-medium">No collections yet.</span> Create your first collection to start grouping elements into themed packs for users.
              </p>
            </div>

            {/* Grid preview (faded placeholders + add card) */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              <AddCollectionCard onClick={() => {}} />
              {PLACEHOLDER_COLLECTIONS.map(c => (
                <PlaceholderCard key={c.name} collection={c} />
              ))}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            <AddCollectionCard onClick={() => {}} />
            {collections.map(c => (
              <div key={c.id} className="group relative rounded-2xl overflow-hidden bg-white border border-gray-100 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 cursor-pointer">
                <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
                  <Package className="w-10 h-10 text-gray-300" />
                </div>
                <div className="p-4">
                  <p className="font-semibold text-gray-900 text-sm truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{c.element_count ?? 0} elements</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCollections;
